/**
 * Typed HTTP client for the session endpoints.
 *
 * Every path is relative, so the bundle works unchanged when the Rust server
 * serves it from its own origin (production) and behind the Vite dev proxy
 * (development). That is not a convenience: the server validates `Origin`
 * against its configured public origin on the exchange, the logout, and the
 * WebSocket upgrade, and the session cookie is `SameSite=Strict`. A
 * cross-origin call would be rejected even with a valid cookie, so no base-URL
 * constant may be introduced here.
 *
 * Wire shapes come exclusively from `src/generated/`, which is produced from
 * `server/src/protocol.rs`.
 */

import type { ErrorBody, YouInfo } from '../generated';

/**
 * A failure that is safe to show a user. `message` is either the backend's
 * `ErrorBody.message` or a fixed local string — never a stack trace, fetch
 * exception text, or raw response body.
 */
export class ApiError extends Error {
  /** Machine-readable reason: backend `ErrorBody.reason` or a local kind. */
  readonly reason: string;
  /** HTTP status, or `null` when the request never produced a response. */
  readonly httpStatus: number | null;

  constructor(message: string, reason: string, httpStatus: number | null) {
    super(message);
    this.name = 'ApiError';
    this.reason = reason;
    this.httpStatus = httpStatus;
  }

  /** True when the server says this client has no usable session. */
  get isUnauthorized(): boolean {
    return this.httpStatus === 401 || this.httpStatus === 403;
  }
}

function isErrorBody(value: unknown): value is ErrorBody {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<ErrorBody>;
  return typeof candidate.reason === 'string' && typeof candidate.message === 'string';
}

function genericMessage(httpStatus: number): string {
  if (httpStatus === 401) return 'This invite is not valid any more. Ask for a fresh link.';
  if (httpStatus === 403) return 'The server refused this request from this page.';
  if (httpStatus === 404) return 'This server does not offer that endpoint.';
  if (httpStatus === 429) return 'Too many attempts. Wait a moment and try again.';
  if (httpStatus >= 500) return 'The server hit an internal error handling that request.';
  return 'The server rejected that request.';
}

async function errorFromResponse(response: Response): Promise<ApiError> {
  let parsed: unknown = null;
  try {
    parsed = await response.json();
  } catch {
    parsed = null;
  }
  if (isErrorBody(parsed) && parsed.message.trim() !== '') {
    return new ApiError(parsed.message, parsed.reason, response.status);
  }
  return new ApiError(genericMessage(response.status), `http-${response.status}`, response.status);
}

async function request(path: string, init: RequestInit): Promise<Response> {
  try {
    const response = await fetch(path, { credentials: 'same-origin', ...init });
    if (!response.ok) throw await errorFromResponse(response);
    return response;
  } catch (cause) {
    if (cause instanceof ApiError) throw cause;
    throw new ApiError(
      'Could not reach the WatchAlong server. Check that it is running, then try again.',
      'network-unreachable',
      null,
    );
  }
}

async function readYouInfo(response: Response): Promise<YouInfo> {
  try {
    return (await response.json()) as YouInfo;
  } catch {
    throw new ApiError(
      'The server sent a response this client could not read.',
      'malformed-response',
      response.status,
    );
  }
}

/**
 * `POST /api/session/exchange`: trades an invite secret for the HttpOnly
 * session cookie. The secret only ever travels in this request body.
 */
export async function exchangeSecret(secret: string): Promise<YouInfo> {
  const response = await request('/api/session/exchange', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ secret }),
  });
  return readYouInfo(response);
}

/** `GET /api/session`: the session this browser already holds, or 401. */
export async function fetchSession(): Promise<YouInfo> {
  const response = await request('/api/session', { method: 'GET' });
  return readYouInfo(response);
}

/** `POST /api/session/logout`: clears the cookie server-side (204). */
export async function logoutSession(): Promise<void> {
  await request('/api/session/logout', { method: 'POST' });
}
